// Ported from NearpodNext's `src/helpers/html/sanitizer.js`, reduced to what a TTC session can
// actually receive. See the allowlist notes below before widening or narrowing it: getting this
// wrong either strips a formula in a live classroom or lets authored markup through.

import DOMPurify from 'dompurify'
import isRichTextContent, { CONTENT_TYPE_ATTRIBUTE_NAME } from './richTextContent'

const RICHTEXT_TAGS = ['p', 'br', 'span', 'em', 'strong', 'ul', 'li', 'ol', 'sup', 'sub', 'div', 'h2', 'h3', 'h4']
const RICHTEXT_ATTR = ['style', CONTENT_TYPE_ATTRIBUTE_NAME]

// The stored formula format: <latex data-latex="x^2" data-fs="...">.
const LATEX_TAG = 'latex'
const LATEX_SOURCE_ATTRIBUTE = 'data-latex'
const LATEX_TAGS = [LATEX_TAG]
const LATEX_ATTR = ['data-fs', LATEX_SOURCE_ATTRIBUTE]

// The lists above are the whole allowlist, not an extension of DOMPurify's default one, so what is
// absent is dropped. Notably absent, and why:
//   - `math-field` + `read-only`/`disabled`/`contenteditable`: MathLive editor DOM. A session
//     payload cannot contain it, and `contenteditable` in student-facing content is undesirable.
//   - `img` + `src`/`alt`/`width`/`height`: TTC carries images through `media.type === 'image'`,
//     never inline in question or answer text. Allowing it would let authored content pull an
//     arbitrary remote URL on the student's device.
//   - MathML tags/attrs and the TinyMCE serialization fixups in `mathMLHelper.js`: those repair
//     MathML authored in the old TinyMCE equation plugin. TTC text was plain until this feature,
//     so no TTC value can hold MathML.
// The backend sanitizer (`html-treatment/src/sanitizer.js`) uses ADD_TAGS and allows `a`/`href`/
// `target` and MathML, so more than this can reach us and this file is the only place the narrowing
// is enforced. Widen it only alongside a matching change on the creation side.
const sanitizeRichText = (html: string): string =>
	DOMPurify.sanitize(html, {
		ALLOWED_TAGS: [...RICHTEXT_TAGS, ...LATEX_TAGS],
		ALLOWED_ATTR: [...RICHTEXT_ATTR, ...LATEX_ATTR],
		// ALLOWED_ATTR alone does not cover data-*, which DOMPurify allows wholesale by default. Off
		// here so TinyMCE-internal bookkeeping (`data-mce-style`, `data-mce-bogus`) cannot be trusted
		// on rendered, user-authored HTML; see NXCORE-2050 / NXCORE-2051 in NearpodNext. The data-*
		// attributes listed above still pass, because they are listed.
		ALLOW_DATA_ATTR: false,
		// Authored content must not be able to relabel itself for assistive tech: this feature exists
		// to give screen readers the formula's speech, so an authored `aria-label` can only lie.
		ALLOW_ARIA_ATTR: false,
	})

// <sup>/<sub> carry meaning that is lost when tags are stripped, so spell them out before the
// markup goes away.
const cleanScriptTags = (text: string): string =>
	text.replace(/<sup>/g, '^(').replace(/<\/sup>/g, ')').replace(/<sub>/g, '_(').replace(/<\/sub>/g, ')')

const NON_BREAKING_SPACE = new RegExp(String.fromCharCode(160), 'g')

// Tag-stripping alone deletes formulas outright: <latex> keeps its content in `data-latex` and has
// no text of its own, so a tags-only pass turns "x = ½" into "x = ". Substituting the TeX source
// keeps it lossless. TeX is a poor thing to read aloud, but callers only rely on this until
// MathJax's speech is available — extractMathSpeech needs typeset DOM, which the string-only
// contexts using this helper do not have.
const htmlToPlainText = (html: string): string => {
	const container = document.createElement('div')
	// Sanitized before the assignment, which writes to innerHTML.
	container.innerHTML = sanitizeRichText(cleanScriptTags(html))
	container.querySelectorAll(LATEX_TAG).forEach((node) => {
		node.replaceWith(document.createTextNode(node.getAttribute(LATEX_SOURCE_ATTRIBUTE) || ''))
	})
	// textContent decodes entities, so &lt;/&gt; need no handling. Non-breaking spaces become normal
	// spaces, then the \s+ collapse handles runs; this diverges deliberately from NearpodNext's
	// `removeLinebreaks` and html-treatment, which delete the entity and so read "x&nbsp;=&nbsp;5"
	// aloud as "x=5".
	return (container.textContent || '').replace(NON_BREAKING_SPACE, ' ').replace(/\s+/g, ' ').trim()
}

// Plain text out of a field that may hold either kind, without the caller needing to know which.
// A no-op on values that are already plain: running those through htmlToPlainText would swallow
// literal `<`/`>` inequality signs as if they opened a tag.
const ensurePlainText = (content = ''): string => (isRichTextContent(content) ? htmlToPlainText(content) : content)

export default sanitizeRichText
export { ensurePlainText }
